import React from "react";
import classnames from "classnames";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import {
  DivideByKeyResultTypes,
  extractParseName,
  extractShortName,
  formatCellphone
} from "../../../utils";
import { IContact } from "../../../types/contacts";

interface ContactItemProps {
  contact: IContact;
  onSelectChat: (id: number) => void;
}

const ContactItem = ({ contact, onSelectChat }: ContactItemProps) => {

  // console.log("render ContactItem", contact.id);

  const idRef = React.useRef(contact.id);

  React.useEffect(() => {
    idRef.current = contact.id;
  }, [contact.id]);

  const handleClick = React.useCallback(() => {
    onSelectChat(idRef.current);
  }, [onSelectChat]);


  const shortName = React.useMemo(() => {
    const { firstName, lastName } = extractParseName(contact.name);
    return extractShortName(firstName, lastName).shortName;
  }, [contact.name]);

  const colors = [
    "bg-primary",
    "bg-danger",
    "bg-info",
    "bg-warning",
    "bg-secondary",
    "bg-pink",
    "bg-purple",
  ];

  const color = contact.id % colors.length;
  const unRead = contact.unreadCount ?? 0;

  const getBadgeColor = () => {
    if (unRead >= 6) return "danger";
    if (unRead >= 3) return "warning";
    return "secondary";
  };

  return (
    <li onClick={handleClick}>
      <div className="d-flex align-items-center">

        <div className="flex-shrink-0 me-2">
          <div className="avatar-xs">
            {contact.avatar ? (
              <img
                src={contact.avatar}
                alt=""
                className="img-fluid rounded-circle"
              />
            ) : (
              <span
                className={classnames(
                  "avatar-title",
                  "rounded-circle",
                  "font-size-10",
                  "text-uppercase",
                  colors[color]
                )}
              >
                {shortName}
              </span>
            )}
            <span className="user-status"></span>
          </div>
        </div>

        <div className="flex-grow-1">
          <h5 className="font-size-14 m-0">{contact.name}</h5>
          <small className="text-muted">
            {formatCellphone(contact.phoneNumber)}
          </small>
        </div>

        {unRead > 0 && (
          <div className="ms-2 me-2">
            <Badge
              color={getBadgeColor()}
              className="rounded-circle d-flex justify-content-center align-items-center p-1"
            >
              {unRead}
            </Badge>
          </div>
        )}

        <div className="flex-shrink-0">
          <Dropdown>
            <DropdownToggle tag="a" className="text-muted">
              <i className="bx bx-dots-vertical-rounded align-middle"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem>Editar</DropdownItem>
              <DropdownItem>Bloquear</DropdownItem>
              <DropdownItem>
                Excluir
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>

      </div>
    </li>
  );
};



interface CharacterItemProps {
  letterContacts: DivideByKeyResultTypes<IContact>;
  index: number;
  onSelectChat: (id: number) => void;
}

const CharacterItem = ({
  letterContacts,
  index,
  onSelectChat,
}: CharacterItemProps) => {

  return (
    <div className="mt-3">
      <div className={classnames({ "mt-3": index !== 0 })}>
        <div className="contact-list-title">
          {letterContacts.letter}
        </div>

        <ul className="list-unstyled contact-list">
          {(letterContacts.data || []).map((contact: IContact) => (
            <MemoContactItem
              key={contact.id}
              contact={contact}
              onSelectChat={onSelectChat}
            />
          ))}
        </ul>

      </div>
    </div>
  );
};


const MemoContactItem = React.memo(
  ContactItem,
  (prev, next) => {

    return (
      prev.contact.id === next.contact.id &&
      prev.contact.unreadCount === next.contact.unreadCount &&
      prev.contact.lastMessageTime === next.contact.lastMessageTime &&
      prev.contact.lastMessageText === next.contact.lastMessageText &&
      prev.contact.blockedMessage === next.contact.blockedMessage
    );

  }
);

// export default React.memo(CharacterItem);

export default React.memo(
  CharacterItem,
  (prev, next) => {

    if (prev.letterContacts.letter !== next.letterContacts.letter)
      return false;

    const prevData = prev.letterContacts.data;
    const nextData = next.letterContacts.data;

    if (prevData.length !== nextData.length)
      return false;

    for (let i = 0; i < prevData.length; i++) {
      if (
        prevData[i].unreadCount !== nextData[i].unreadCount ||
        prevData[i].lastMessageTime !== nextData[i].lastMessageTime ||
        prevData[i].lastMessageText !== nextData[i].lastMessageText
      ) {
        return false;
      }
    }

    return true;
  }
);